import { faTrash } from "@fortawesome/free-solid-svg-icons";
import HeaderActionConfig from "../HeaderActionConfig";
import { useLanguageContext } from "../../../../../../context/LanguageContext";
import StringHelper from "../../../../../../helpers/StringHelper";
import useEvaApi, { EvaApiCall } from "../../../../../../hooks/useEvaApi";
import { useAuthContext } from "../../../../../../context/AuthContext";
import { StyledAltButton } from "../../../../../../styles/ButtonStyles";

/**
 * @param {string} entity for example: "location"
 * @param {function(row, dataTable) : string} url defaults to `entityName (plural)`
 * @param {function(response, row, dataTable) : void} onSuccess defaults to null
 * @returns {HeaderActionConfig}
 */
function useDeleteHeaderActionConfig(entity, url, onSuccess = null) {
  const callEvaApi = useEvaApi();
  const authContext = useAuthContext();
  const { translate } = useLanguageContext();

  /**
   * This method will try to delete the entities from the database
   * @param {object} dataTable
   * @returns
   */
  async function onDelete(dataTable) {
    //Ask the user if he is sure
    if (!window.confirm(translate("eva.main.questions.delete"))) {
      return;
    }

    //Get the url path and id from the path
    const deleteUrl = getDeleteUrl(dataTable);

    //Delete the entity
    await callEvaApi(
      new EvaApiCall(deleteUrl)
        .setMethod("POST")
        .setData({ ids: dataTable.selectedRows })
        .setLoadingGroup(`${dataTable.config.getDataTableKey()}_delete`)
        .setOnSuccess((response) => {
          if (onSuccess) {
            onSuccess(response, dataTable);
          }
        })
    );

    //Reload the data table
    dataTable.fetch();
  }

  /**
   * This method will determine the url for the delete action
   * @param {object} row
   * @returns {string}
   */
  function getDeleteUrl(dataTable) {
    return url
      ? url(dataTable)
      : StringHelper.pluralize(entity) + "/delete/multiple";
  }

  /**
   * This method will determine if the delete button is disabled
   * @returns {bool}
   */
  function getDisabled(dataTable) {
    return (
      dataTable.selectedRows.length === 0 ||
      !authContext.hasPermission(`${StringHelper.pluralize(entity)}.delete`)
    );
  }

  return new HeaderActionConfig()
    .setIcon(faTrash)
    .setLabel((dataTable) => ` (${dataTable.selectedRows.length})`)
    .setCallback(onDelete)
    .setDisabled((dataTable) => getDisabled(dataTable))
    .setElement(StyledAltButton)
    .setLoadingGroups(
      (dataTable) => `${dataTable.config.getDataTableKey()}_delete`
    )
    .setTitle(
      translate("eva.main.entities.states.delete", [
        {
          key: "entities",
          value: translate(
            `eva.main.entities.${StringHelper.pluralize(entity)}`
          ),
        },
      ])
    );
}

export default useDeleteHeaderActionConfig;
